import { refresh } from './utils/views';

/**
 * @typedef {Object} Config
 * @property {number} maxRetries - Maximum number of retries for fetching entitlements.
 * @property {number} backOffDelay - Initial delay in milliseconds for retry backoff.
 * @property {number} entitlementsTtl - Time-to-live for entitlements in milliseconds.
 * @property {string} entitlementsEndpoint - Endpoint URL to fetch entitlements.
 * @property {Object} fetchOptions - Options for the fetch request.
 * @property {string} [entitlementsKey] - Name for localStorage item.
 * @property {string} [expiresKey] - Key to look for an expiry time in the entitlement response.
 */

/**
 * Initialises the component when passed an element
 *
 * @param {Object} root The component dom node
 */
export default async function init(root) {
  if (!root) {
    return;
  }

  /** @type {Config} */
  const config = window.sdc.authManager.config;
  let userData = {};
  const views = [];
  const loggedIn = document.cookie.indexOf('loggedIn=') !== -1;

  root.authManager = {
    addView(view) {
      views.push(view);
      refresh(views, userData, loggedIn);
    }
  };

  const entitlementsKey = config.entitlementsKey || 'entitlements';
  const entitlementsExpiry = `${entitlementsKey}-expiry`;

  if (!loggedIn) {
    localStorage.removeItem(entitlementsKey);
    localStorage.removeItem(entitlementsExpiry);
    return;
  }

  const entitlementData = localStorage.getItem(entitlementsKey);
  const entitlementsTtl = parseInt(localStorage.getItem(entitlementsExpiry), 10);

  const beforeTtl = Date.now() < entitlementsTtl;
  const pastTtl = Date.now() > entitlementsTtl;

  // For debugging
  window.sdc.authManager.timing = {
    secondsTillRefresh: (Date.now() - entitlementsTtl) / 1000,
    minutesTillRefresh: (Date.now() - entitlementsTtl) / 1000 / 60,
  };

  if (entitlementData && beforeTtl) {
    userData = JSON.parse(entitlementData);
    refresh(views, userData, loggedIn);
    return;
  }

  if (!entitlementData || pastTtl) {
    const data = await getEntitlements(config);

    localStorage.setItem(entitlementsKey, JSON.stringify(data));

    let expiry = Date.now() + config[config.expiresKey || 'entitlementsTtl'];
    if (data.expires) {
      expiry = data.expires;
    }
    localStorage.setItem(entitlementsExpiry, String(expiry));

    userData = data;

    refresh(views, userData, loggedIn);
  }
}

async function getEntitlements(config) {
  const response = await retry(config.entitlementsEndpoint, config.fetchOptions);
  const data = await response.json();

  if (response.status !== 200) {
    throw new Error(data.message);
  }

  return data;
}

function retry(url, options) {
  const waitLocalStorageKey = 'uiAuthManagerBackoffWait';
  const retryLocalStorageKey = 'uiAuthManagerNextRetryTime';

  function incrementBackoff() {
    let currentBackoff = getLocalStorageItem(waitLocalStorageKey)
        ? parseInt(getLocalStorageItem(waitLocalStorageKey), 10) : 500;

    currentBackoff = currentBackoff >= 128000 ? 128000 : currentBackoff * 2;
    setLocalStorageItem(waitLocalStorageKey, currentBackoff);
    setLocalStorageItem(retryLocalStorageKey, Date.now() + (currentBackoff));

    return currentBackoff;
  }

  return new Promise(async (resolve) => {
    function get() {
      fetch(url, options).then((res) => {
        if (res.status !== 200) {
          throw new Error(`Status code is: ${res.status}`);
        }
        resolve(res);
      }).catch(() => {
        setTimeout(get, incrementBackoff() + (Math.random() * 2500));
      });
    }

    const timeRemaining = getLocalStorageItem(retryLocalStorageKey) - Date.now();
    if (getLocalStorageItem(retryLocalStorageKey) && timeRemaining > 0) {
      setTimeout(get, timeRemaining);
    } else {
      get();
    }
  });
}

function getLocalStorageItem(key) {
  return localStorage.getItem(key);
}

function setLocalStorageItem(key, value) {
  return localStorage.setItem(key, value);
}
